import dayjs from "dayjs";
import { set } from "lodash";

import * as React from "react";

import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Tooltip } from "@mui/material";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";

import { AIRLINE_IMG, AIRLINE_NAME_MAP } from "@/constants/airline";
import { AIRPORT_MAP } from "@/constants/airport";
import { NEED_BACKGROUND_PROGRAM, airlineBgColor } from "@/constants/config";
import { PROGRAM_NAME_MAP } from "@/constants/programs";
import { PROGRAM_IMG } from "@/constants/programs";
import { PARTIAL_DISPLAY_ITEM_NUM } from "@/constants/membership";
import useThemeData from "@/hooks/useThemeData";
import { FlightItem } from "@/typings";
import { formatPts } from "@/utils";

import ExpandBtn from "../FlightItem/ExpandBtn";
import ProductInfo from "../FlightItem/ProductInfo";
import SummaryOptions from "../FlightItem/SummaryOptions";
import SitemapDialogBtn from "../SitemapDialog";

function Row(props: {
  index: number;
  row: FlightItem;
  onOpenOptionDialog: (data: FlightItem) => void;
  onOpenRouteDialog?: (data: FlightItem) => void;
  onCabinClick?: (data: FlightItem) => void;
  partialDisplay?: boolean;
}) {
  const { row, onOpenRouteDialog, partialDisplay } = props;
  const theme = useThemeData();
  const [open, setOpen] = React.useState(false);

  const formatTime = React.useCallback((minutes: number) => {
    if (minutes > 60) {
      return `${Math.floor(minutes / 60)}hr ${minutes % 60}min`;
    } else if (minutes > 0) {
      return `${minutes} min`;
    } else {
      return "-";
    }
  }, []);

  const products = row?.fare?.products || [];
  // Count the total duration of the flight airtime
  const total_airtime = products.reduce((acc, item) => acc + item.travel_minutes, 0);
  const seats = row?.ss == -1 ? "-" :  row?.ss + "+";

  const airportInfo = products.map((item) => `${item.origin} - ${item.destination}`);

  const airportTipsInfo = products.map((item) => {
    return `${AIRPORT_MAP[item.origin]?.name || item.origin}(${item.origin}) - ${
      AIRPORT_MAP[item.destination]?.name || item.destination
    }(${item.destination})`;
  });

  const darkListBackground = theme.isDarkMode ? "#2D2D2D" : "#F5F5F5";
  // Get current path
  const currentPath = window.location.pathname;
  const showSeen = currentPath === "/routes-flights" || currentPath === "/alert-routes-flights";

  return (
    <React.Fragment>
      <TableRow
        sx={{
          "& > *": { borderBottom: "unset", padding: "0px 0px", paddingLeft: "0px" }, // Reduced padding for all cells
          ":first-child": {
            backgroundColor: row.isTop ? "theme.palette.primary.main" : darkListBackground,
          },
          backgroundColor: props.index % 2 == 0 ? darkListBackground : "transparent",
        }}
        onClick={() => setOpen(!open)}
      >
        {/* Program */}
        <TableCell align="center" component="th" scope="row" width={100} sx={{ padding: "0px 0px" }}>
          <Tooltip title={PROGRAM_NAME_MAP.get(row.program_code)} placement="top" arrow>
            <div className="flex items-center py-[3px] pl-[10px]">
              <Box
                className="rounded-[3px] p-[6px]  flex items-center h-[30px]"
                sx={{
                  backgroundColor:
                    theme.isDarkMode && true
                      ? airlineBgColor
                      : "transparent",
                }}
              >
                <img className="h-auto w-[48px]" src={PROGRAM_IMG[row.program_code]} alt="" />
              </Box>
              <span className="ml-[10px]">{row.program_code}</span>
            </div>
          </Tooltip>
        </TableCell>
        {/* Flight */}
        <TableCell width={180} align="center" sx={{ padding: "0px 0px" }}>
          <Tooltip
            title={
              <div>
                {products.map((item, index) => (
                  <div key={index}>{AIRLINE_NAME_MAP[item.airline_code]}</div>
                ))}
              </div>
            }
            placement="top"
            arrow
          >
            <div className="space-y-[4px] flex flex-col items-center">
              {products.map((item, index) => (
                <div key={index} className="flex items-center">
                  <Box
                    className="rounded-[3px] p-[6px] flex items-center h-[20px]"
                    sx={{
                      backgroundColor: theme.isDarkMode ? airlineBgColor : "transparent",
                      fontSize: 0,
                    }}
                  >
                    <img src={AIRLINE_IMG[item.airline_code]} className="w-[19px] h-auto" />
                  </Box>
                  <span className="ml-[5px] w-[50px]">{item.flight_number}</span>
                </div>
              ))}
            </div>
          </Tooltip>
        </TableCell>
        {/* Route */}
        <TableCell align="center" width={160} sx={{ padding: "0px 0px" }}>
          <Tooltip
            title={
              <div className="flex flex-col">
                {airportTipsInfo.map((item, index) => (
                  <div key={index}>{item}</div>
                ))}
              </div>
            }
            placement="top"
            arrow
          >
            <div className="flex flex-col">
              {airportInfo.map((item, index) => (
                <div key={index}>{item}</div>
              ))}
            </div>
          </Tooltip>
        </TableCell>
        {showSeen && (
          <TableCell align="center" width={180} sx={{ padding: "0px 0px" }}>
            {dayjs(row.ls * 1000)
              .fromNow()
              .replace("hours", "hrs")
              .replace("minutes", "mins")}
          </TableCell>
        )}
        <TableCell align="center" width={180} sx={{ padding: "0px 0px" }}>
          {products.map((item, index) => (
            // <div key={index}>{item.cabin_type}</div>
            <div key={index} className="flex items-center justify-center">
              {dayjs(item.departure_time).format("M/D hh:mmA")}
            </div>
          ))}
          {/* // {dayjs(row.date).format("M/D")} {dayjs(products[0].departure_time).format("hh:mmA")} */}
        </TableCell>
        {/* <TableCell align="center" width={220}>
          {dayjs(row.date).format("ddd M/D")}
          <div>{dayjs(products[products.length - 1].arrival_time).format("hh:mmA")}</div>
        </TableCell> */}
        <TableCell align="center" width={180} sx={{ padding: "0px 0px" }}>
          {products.map((item, index) => (
            // <div key={index}>{item.cabin_type}</div>
            <div key={index} className="flex items-center justify-center">
              {dayjs(item.arrival_time).format("M/D hh:mmA")}
            </div>
          ))}
          {/* // {dayjs(row.date).format("M/D")} {dayjs(products[0].departure_time).format("hh:mmA")} */}
        </TableCell>
        <TableCell width={125} align="center" sx={{ padding: "0px 0px" }}>
          {products.map((item, index) => (
            <div key={index}>{formatTime(item.travel_minutes || 0)}</div>
          ))}
        </TableCell>
        {/* <TableCell width={205} align="center" sx={{ padding: "0px 0px" }}>
          {products.map((item, index) => (
            <div key={index}>{formatTime(item.travel_minutes || 0)} ({Math.round(item.travel_minutes/total_airtime*100)}%)</div>
          ))}
        </TableCell> */}
        <TableCell width={140} align="center" sx={{ padding: "0px 0px" }}>
          {products.map((item, index) => (
            <div key={index}>
              {item.cabin_type == "Premium Economy" ? "Premium" : item.cabin_type}
            </div>
          ))}
        </TableCell>
        {/* <TableCell align="center" width={180}>
          <Tooltip
            title={
              <div>
                {products.map((item, index) => (
                  <div key={index}>{item.aircraft}</div>
                ))}
              </div>
            }
            placement="top"
            arrow
          >
            <div>
              {products.map((item, index) => (
                <div key={index}>{item.aircraft.split("-")[0]}</div>
              ))}
            </div>
          </Tooltip>
        </TableCell> */}
        <TableCell align="center" width={180} sx={{ padding: "0px 0px" }}>
          {products.map((item, index) => (
            <div key={index}>{formatTime(item.layover_time || 0)}</div>
          ))}
        </TableCell>
        <TableCell align="center" width={150} sx={{ padding: "0px 0px" }}>
          {row.fare.travel_time_display}
        </TableCell>
        <TableCell align="center" width={50} sx={{ padding: "0px 0px" }}>
          <div key={seats}>{seats}</div>
        </TableCell>
        <TableCell align="center" width={50} sx={{ padding: "0px 0px" }}>
          <SitemapDialogBtn data={row} type="icon" />
        </TableCell>
        
        {/* <TableCell align="center">{row.percent_premium || "-"}</TableCell> */}
        {/* <TableCell align="center" width={180}>
          {formatPts(row.award_points)} +${row.surcharge}
        </TableCell> */}
        {/* <TableCell align="center" width={100}>
          {row.ss == -1 ? "-" : row.ss == 9 ? row.ss + "+" : row.ss}
        </TableCell> */}
        <TableCell align="center" width={180} sx={{ padding: "0px 0px" }}>
          <Typography 
            variant="body2" 
            sx={{ 
              fontWeight: "bold", 
              marginLeft: "12px",
              filter: partialDisplay ? "blur(3px)" : "none"
            }}
          >
            {formatPts(row.award_points)}
          </Typography>

          <Typography variant="body2" color="textSecondary" mt={"2px"} ml={"12px"}>
            {row.surcharge > 0 && `+$${row.surcharge}`}
          </Typography>
        </TableCell>

        <TableCell align="center" sx={{ padding: "0px 0px" }}>
          <div className="flex flex-col">
            {onOpenRouteDialog && (
              <IconButton
                size="small"
                fontSize="13px"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenRouteDialog(row);
                }}
              >
                <AutoAwesomeRoundedIcon fontSize={"13px"} color="primary" />
              </IconButton>
            )}
            <div>
              <IconButton
                aria-label="expand"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  props.onOpenOptionDialog(props.row);
                }}
                fontSize="13px"
                sx={{ padding: 0.5 }}
              >
                <InfoOutlinedIcon fontSize="13px" />
              </IconButton>
            </div>
            {!onOpenRouteDialog && (
              <div>
                <ExpandBtn
                  onClick={() => {
                    setOpen(!open);
                  }}
                  fontSize="13px"
                  sx={{ padding: 0 }}
                  isExpanded={open}
                />
              </div>
            )}
          </div>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={12}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <div className="flex justify-between">
                <div>
                  {products.map((product, i) => (
                    <ProductInfo
                      key={i}
                      data={row}
                      product={product}
                      isLast={i === products.length - 1}
                    />
                  ))}
                </div>
                <div className="flex items-center">
                  <SummaryOptions data={row} />
                </div>
              </div>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

interface Props {
  data: FlightItem[];
  hiddenHead?: boolean;
  onOpenOptionDialog: (data: FlightItem) => void;
  onOpenRouteDialog?: (data: FlightItem) => void;
  partialDisplay?: boolean;
}
export default function CollapsibleTable(props: Props) {
  let { data, partialDisplay = false } = props;
  const { hiddenHead = false } = props;
  const currentPath = window.location.pathname;
  const showSeen = currentPath === "/routes-flights" || currentPath === "/alert-routes-flights";
  try {
    data.forEach((item) => {
      if (!item.hash) {
        item.hash = Math.random().toString();
      }
    });
  } catch (error) {
    console.error("Error while generating hash for flight items:", error);
    // Ensure data is always an array even if error occurs
    data = data || [];
  }
  return (
    <>
      <TableContainer component={Paper}>
        <Table aria-label="collapsible table" sx={{ minWidth: "1000px" }}>
          {!hiddenHead && (
            <TableHead>
              <TableRow>
                <TableCell align="center">Program</TableCell>
                <TableCell align="center">Flight</TableCell>
                <TableCell align="center">Route</TableCell>
                {showSeen && <TableCell align="center">Seen</TableCell>}
                <TableCell align="center">Departure</TableCell>
                <TableCell align="center">Arrival</TableCell>
                <TableCell align="center">Airtime</TableCell>

                <TableCell align="center">Cabin</TableCell>
                {/* <TableCell align="center">Aircraft</TableCell> */}
                <TableCell align="center">Connect</TableCell>
                <TableCell align="center">Duration</TableCell>
                <TableCell align="center">Seats</TableCell>
                <TableCell align="center">Map</TableCell>
                {/* <TableCell align="center">Seat</TableCell> */}
                <TableCell align="center">Cost</TableCell>
                {/* <TableCell align="center">Seen</TableCell> */}
                <TableCell align="center">Detail</TableCell>
              </TableRow>
            </TableHead>
          )}
          <TableBody
            sx={{
              ".MuiTableCell-root": { paddingY: "10px" },
            }}
          >
            {data.map((row, index) => (
              <Row
                index={index}
                key={row.hash}
                row={row}
                partialDisplay={index >= PARTIAL_DISPLAY_ITEM_NUM && partialDisplay}
                onOpenOptionDialog={props.onOpenOptionDialog}
                onOpenRouteDialog={props.onOpenRouteDialog}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
